import React, { useEffect, useState } from "react";
import { usePolicyManagement } from "./hooks/usePolicyManagement";
import { FEATURES, FeatureGate } from "../../lib/utils/featureAccess";
import DataTable from "../../lib/components/DataTable";
import { Edit, Trash2, SlidersHorizontal, Plus, X, Eye } from "lucide-react";
import { Button, Box, Snackbar, Alert, Chip } from "@mui/material";
import { EFFECT_OPTIONS, AVAILABLE_PERMISSIONS } from "./constants";

// Static computations — AVAILABLE_PERMISSIONS never changes at runtime
const permissionsByFeature = AVAILABLE_PERMISSIONS.reduce((acc, perm) => {
  const feature = perm.value.split(":")[0];
  if (!acc[feature]) acc[feature] = [];
  acc[feature].push(perm);
  return acc;
}, {});

const permLabelMap = Object.fromEntries(AVAILABLE_PERMISSIONS.map(p => [p.value, p.label]));
const getPermLabel = permValue => permLabelMap[permValue] || permValue;

const PolicyManagement = () => {
  const { policies, loading, selectedPolicy, createPolicy, updatePolicy, deletePolicy, selectPolicy, clearError } =
    usePolicyManagement();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewPolicy, setViewPolicy] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [formData, setFormData] = useState({
    policyId: "",
    name: "",
    description: "",
    statements: [{ effect: "ALLOW", permissions: [] }],
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (selectedPolicy) {
      setFormData({
        policyId: selectedPolicy.policyId || "",
        name: selectedPolicy.name || "",
        description: selectedPolicy.description || "",
        statements: selectedPolicy.statements?.length
          ? selectedPolicy.statements
          : [{ effect: "ALLOW", permissions: [] }],
      });
      setShowCreateModal(true);
    }
  }, [selectedPolicy]);

  const columns = [
    { key: "policyId", label: "Policy ID", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "description", label: "Description", sortable: true },
    {
      key: "statements",
      label: "Statements",
      sortable: false,
      render: value => {
        if (!value || !Array.isArray(value)) return <span className="text-sm text-gray-400">None</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((stmt, idx) => {
              const permCount = stmt.permissions?.length || 0;
              const isAllow = stmt.effect === "ALLOW";
              return (
                <Chip
                  key={idx}
                  label={`${stmt.effect}: ${permCount} perm${permCount !== 1 ? "s" : ""}`}
                  size="small"
                  color={isAllow ? "success" : "error"}
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              );
            })}
          </div>
        );
      },
    },
    {
      key: "system",
      label: "System",
      sortable: true,
      render: value => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            value ? "bg-accent-light text-accent-dark" : "bg-gray-100 text-gray-600"
          }`}
        >
          {value ? "System" : "Custom"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      filterable: false,
      render: (value, row) => (
        <div className="flex gap-2">
          <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
            <button
              onClick={() => {
                setViewPolicy(row);
                setShowViewModal(true);
              }}
              className="text-gray-600 hover:text-blue-600"
              aria-label={`View policy ${row.name}`}
            >
              <Eye size={18} />
            </button>
            <button
              onClick={() => selectPolicy(row)}
              className="text-gray-600 hover:text-accent"
              aria-label={`Edit policy ${row.name}`}
            >
              <Edit size={18} />
            </button>
            <button
              onClick={async () => {
                if (window.confirm(`Are you sure you want to delete policy "${row.name}"?`)) {
                  try {
                    await deletePolicy(row.policyId);
                    setToastMessage("Policy deleted successfully");
                    setShowToast(true);
                  } catch {
                    setToastMessage("Failed to delete policy");
                    setShowToast(true);
                  }
                }
              }}
              className="text-red-600 hover:text-red-800"
              aria-label={`Delete policy ${row.name}`}
            >
              <Trash2 size={18} />
            </button>
          </FeatureGate>
        </div>
      ),
    },
  ];

  const handleSubmit = async e => {
    e.preventDefault();
    // Filter out empty statements
    const cleanedStatements = formData.statements.filter(s => s.permissions && s.permissions.length > 0);
    const payload = { ...formData, statements: cleanedStatements };

    try {
      if (selectedPolicy) {
        await updatePolicy(selectedPolicy.policyId, payload);
        setToastMessage("Policy updated successfully");
      } else {
        await createPolicy(payload);
        setToastMessage("Policy created successfully");
      }
      setShowToast(true);
      setShowCreateModal(false);
      selectPolicy(null);
      setFormData({ policyId: "", name: "", description: "", statements: [{ effect: "ALLOW", permissions: [] }] });
      clearError();
    } catch {
      setToastMessage("Operation failed");
      setShowToast(true);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStatementEffectChange = (index, effect) => {
    setFormData(prev => ({
      ...prev,
      statements: prev.statements.map((s, i) => (i === index ? { ...s, effect } : s)),
    }));
  };

  const handleStatementPermToggle = (stmtIndex, permission) => {
    setFormData(prev => ({
      ...prev,
      statements: prev.statements.map((s, i) => {
        if (i !== stmtIndex) return s;
        const perms = s.permissions.includes(permission)
          ? s.permissions.filter(p => p !== permission)
          : [...s.permissions, permission];
        return { ...s, permissions: perms };
      }),
    }));
  };

  const addStatement = () => {
    setFormData(prev => ({
      ...prev,
      statements: [...prev.statements, { effect: "ALLOW", permissions: [] }],
    }));
  };

  const removeStatement = index => {
    setFormData(prev => ({
      ...prev,
      statements: prev.statements.filter((_, i) => i !== index),
    }));
  };

  const handleSelectAll = (stmtIndex, feature) => {
    const featurePerms = AVAILABLE_PERMISSIONS.filter(p => p.value.startsWith(feature + ":")).map(p => p.value);
    setFormData(prev => ({
      ...prev,
      statements: prev.statements.map((s, i) => {
        if (i !== stmtIndex) return s;
        const allSelected = featurePerms.every(p => s.permissions.includes(p));
        const perms = allSelected
          ? s.permissions.filter(p => !featurePerms.includes(p))
          : [...new Set([...s.permissions, ...featurePerms])];
        return { ...s, permissions: perms };
      }),
    }));
  };

  return (
    <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
      <div>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowFilters(prev => !prev)}
            startIcon={<SlidersHorizontal size={15} />}
            sx={{
              textTransform: "none",
              borderColor: showFilters ? "var(--color-accent-active)" : "#d1d5db",
              borderRadius: "10px",
              color: showFilters ? "var(--color-accent-active)" : "#374151",
              backgroundColor: showFilters ? "var(--color-accent-alpha-06)" : "transparent",
              fontWeight: 600,
              fontSize: "0.8rem",
              px: 2,
              py: 0.7,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "var(--color-accent-active)",
                backgroundColor: "var(--color-accent-lightest)",
                color: "var(--color-accent-active)",
              },
            }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
            <button
              onClick={() => {
                selectPolicy(null);
                setFormData({
                  policyId: "",
                  name: "",
                  description: "",
                  statements: [{ effect: "ALLOW", permissions: [] }],
                });
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 btn-accent px-4 py-2 rounded"
            >
              <Plus size={16} />
              Add Policy
            </button>
          </FeatureGate>
        </Box>

        <DataTable columns={columns} data={policies} loading={loading} showFilters={showFilters} pageSize={10} />

        {/* View Policy Modal */}
        {showViewModal && viewPolicy && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Policy Details</h2>

              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Policy ID</span>
                  <p className="text-sm text-gray-900">{viewPolicy.policyId}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Name</span>
                  <p className="text-sm text-gray-900">{viewPolicy.name || "\u2014"}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Description</span>
                  <p className="text-sm text-gray-900">{viewPolicy.description || "\u2014"}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Type</span>
                  <p className="text-sm text-gray-900">{viewPolicy.system ? "System" : "Custom"}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Statements</span>
                  {(!viewPolicy.statements || viewPolicy.statements.length === 0) && (
                    <p className="text-sm text-gray-400 mt-1">None</p>
                  )}
                  {(viewPolicy.statements || []).map((stmt, idx) => (
                    <div key={idx} className="mt-2 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Chip
                          label={stmt.effect}
                          size="small"
                          color={stmt.effect === "ALLOW" ? "success" : "error"}
                          variant="filled"
                          sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                        />
                        <span className="text-xs text-gray-500">
                          {stmt.permissions?.length || 0} permission{(stmt.permissions?.length || 0) !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(stmt.permissions || []).map(perm => (
                          <Chip
                            key={perm}
                            label={getPermLabel(perm)}
                            size="small"
                            variant="outlined"
                            color={stmt.effect === "ALLOW" ? "success" : "error"}
                            sx={{ fontSize: "0.7rem" }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end pt-4 mt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewPolicy(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Policy Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">{selectedPolicy ? "Edit Policy" : "Create Policy"}</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="policyId" className="block text-sm font-medium text-gray-700 mb-1">
                    Policy ID *
                  </label>
                  <input
                    id="policyId"
                    type="text"
                    name="policyId"
                    value={formData.policyId}
                    onChange={handleInputChange}
                    disabled={!!selectedPolicy}
                    placeholder="e.g. policy_fleet_readonly"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Fleet Read-Only Policy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                  />
                </div>

                {/* Policy Statements */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="block text-sm font-medium text-gray-700">Policy Statements</label>
                    <button
                      type="button"
                      onClick={addStatement}
                      className="text-sm text-accent hover:text-accent-dark font-medium"
                    >
                      + Add Statement
                    </button>
                  </div>

                  {formData.statements.map((statement, stmtIdx) => (
                    <div key={stmtIdx} className="border border-gray-200 rounded-lg p-4 mb-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-600">Statement {stmtIdx + 1}</span>
                          <select
                            value={statement.effect}
                            onChange={e => handleStatementEffectChange(stmtIdx, e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-accent"
                          >
                            {EFFECT_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {formData.statements.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeStatement(stmtIdx)}
                            className="text-red-500 hover:text-red-700"
                            title="Remove statement"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>

                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {Object.entries(permissionsByFeature).map(([feature, perms]) => (
                          <div key={feature} className="border-b border-gray-100 pb-2">
                            <div className="flex items-center gap-2 mb-1">
                              <button
                                type="button"
                                onClick={() => handleSelectAll(stmtIdx, feature)}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {perms.every(p => statement.permissions.includes(p.value))
                                  ? "Deselect all"
                                  : "Select all"}
                              </button>
                              <span className="text-xs font-semibold text-gray-500 uppercase">{feature}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {perms.map(perm => {
                                const action = perm.value.split(":")[1];
                                const selected = statement.permissions.includes(perm.value);
                                return (
                                  <Chip
                                    key={perm.value}
                                    label={action}
                                    size="small"
                                    variant={selected ? "filled" : "outlined"}
                                    color={selected ? (statement.effect === "ALLOW" ? "success" : "error") : "default"}
                                    onClick={() => handleStatementPermToggle(stmtIdx, perm.value)}
                                    sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      selectPolicy(null);
                      setFormData({
                        policyId: "",
                        name: "",
                        description: "",
                        statements: [{ effect: "ALLOW", permissions: [] }],
                      });
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 btn-accent-submit rounded-md">
                    {selectedPolicy ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Toast */}
        <Snackbar
          open={showToast}
          autoHideDuration={3000}
          onClose={() => setShowToast(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setShowToast(false)}
            severity={toastMessage.includes("failed") || toastMessage.includes("Failed") ? "error" : "success"}
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </div>
    </FeatureGate>
  );
};

export default PolicyManagement;
